// 📁 src/context/AuthContext.js
import { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Fonction pour vérifier si un token est valide
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      
      // Vérifier si le token n'est pas expiré (avec une marge de 5 minutes)
      return decoded.exp > (now + 300);
    } catch (error) {
      console.error("Erreur lors du décodage du token:", error);
      return false;
    }
  };

  // Fonction pour nettoyer les données d'authentification
  const clearAuth = () => {
    setToken(null);
    setRole(null);
    setUserInfo(null);
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
  };

  // Initialisation au chargement de l'application
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedRole = localStorage.getItem("role");
    const savedUser = localStorage.getItem("user");

    if (savedToken && isTokenValid(savedToken)) {
      // Token valide, restaurer la session
      setToken(savedToken);
      setRole(savedRole);
      if (savedUser) {
        try {
          setUserInfo(JSON.parse(savedUser));
        } catch (error) {
          console.error("Erreur lors du parsing des données utilisateur:", error);
          setUserInfo(null);
        }
      }
    } else if (savedToken) {
      // Token expiré ou invalide, nettoyer
      toast.warning("Session expirée. Veuillez vous reconnecter.", {
        position: "top-right",
        autoClose: 3000,
      });
      clearAuth();
    }
    
    setIsLoading(false);
  }, []);

  const login = (token, role, userData) => {
    if (!isTokenValid(token)) {
      toast.error("Token invalide reçu du serveur");
      return false;
    }

    setToken(token);
    setRole(role);
    setUserInfo(userData);

    localStorage.setItem("token", token);
    localStorage.setItem("role", role);
    localStorage.setItem("user", JSON.stringify(userData));

    toast.success("Connexion réussie !", {
      position: "top-right",
      autoClose: 2000,
    });

    return true;
  };

  const logout = () => {
    clearAuth();
    toast.info("Déconnexion réussie", {
      position: "top-right",
      autoClose: 2000,
    });
    navigate("/login");
  };

  // Vérifier périodiquement la validité du token (toutes les 5 minutes)
  useEffect(() => {
    if (!token) return;

    const checkTokenInterval = setInterval(() => {
      if (!isTokenValid(token)) {
        toast.warning("Session expirée. Redirection vers la page de connexion...", {
          position: "top-right",
          autoClose: 3000,
        });
        clearAuth();
        navigate("/login");
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(checkTokenInterval);
  }, [token, navigate]);

  // Ne pas afficher l'application pendant le chargement initial
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      token, 
      role, 
      userInfo, 
      user: userInfo,
      login, 
      logout, 
      isAuthenticated: !!token && isTokenValid(token) 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

// Hook pratique pour consommer le contexte d'auth
export const useAuth = () => useContext(AuthContext);
